import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { isEmpty, unionBy } from 'lodash';

import {
  initialDefaultTimeboxPrefItemState,
  timeboxPrefItemReducer,
  routinePrefItemReducer,
  tasksPrefItemReducer,
  transformDBPrefToTimeboxPref,
  transformDBPrefToRoutine,
  transformTodosToTaskPref,
  workHourPrefItemReducer,
  initialDefaultWorkHourPrefItemState,
  transformDBPrefToWorkHourPref,
  TasksPrefItemState,
} from '../../components';
import {
  usePrepareScheduler,
  FirestoreRoutineActivity,
  useTodoTasks,
  useOverdueTasks,
  useAuthContext,
  useTodoTasksContext,
  getArrayOfXDaysForwardFromToday,
} from '../../data-access';
import { taskFilterAtom } from '../../data-access/recoil';
import { useRecoilValue } from 'recoil';
import { OVERDUE_TASK_RECENT_DAY_THRESHOLD } from './utils';

const PREFETCH_DATES_COUNT = 3;
const WEEK_OVERDUE_LIMIT = 200;

export const usePreferenceManagement = (
  selectedDate: Dayjs,
  schedulerScope: 'day' | 'week' = 'day',
  rangeEndDate?: Dayjs
) => {
  const { user } = useAuthContext();
  const taskFilter = useRecoilValue(taskFilterAtom);
  const {
    overDueDateTill,
    isFetchingOverdueTasks,
    todoTasksByDate,
    overdueTasks,
    visibleProjectIds,
  } = useTodoTasksContext();
  const {
    schedulerPreferences,
    routine,
    isRoutineLoading,
    isPreferenceLoading,
    addRoutineActivity,
    hideRoutine,
    updatePreferences,
  } = usePrepareScheduler();

  const [timeBoxPrefItems, dispatchTimeBoxPrefItems] = useReducer(
    timeboxPrefItemReducer,
    initialDefaultTimeboxPrefItemState
  );
  const [routinePrefItem, dispatchRoutinePrefItem] = useReducer(routinePrefItemReducer, {});
  const [tasksPrefItems, dispatchTasksPrefItems] = useReducer(tasksPrefItemReducer, {});
  const [workHourPrefItem, dispatchWorkHourPrefItem] = useReducer(
    workHourPrefItemReducer,
    initialDefaultWorkHourPrefItemState
  );
  const tasksPrefItemsRef = useRef<TasksPrefItemState>({});
  const [tasksPrefSyncedKey, setTasksPrefSyncedKey] = useState<string | null>(null);
  const [routinePrefSynced, setRoutinePrefSynced] = useState(false);
  const isWeekScope = schedulerScope === 'week';
  const selectedDateKey = selectedDate.format('YYYY-MM-DD');

  const weekRange = useMemo(() => {
    if (!isWeekScope) {
      return null;
    }
    if (rangeEndDate) {
      const normalizedStart = selectedDate.startOf('day');
      const normalizedEnd = rangeEndDate.isBefore(normalizedStart, 'day')
        ? normalizedStart
        : rangeEndDate.startOf('day');
      return { startDate: normalizedStart, endDate: normalizedEnd };
    }
    const currentWeekMonday = selectedDate.startOf('week').add(1, 'day');
    const currentWeekFriday = currentWeekMonday.add(4, 'day');
    const startDate = selectedDate.isAfter(currentWeekMonday, 'day')
      ? selectedDate
      : currentWeekMonday;

    if (startDate.isAfter(currentWeekFriday, 'day')) {
      const nextMonday = currentWeekMonday.add(7, 'day');
      return { startDate: nextMonday, endDate: nextMonday.add(4, 'day') };
    }

    return { startDate, endDate: currentWeekFriday };
  }, [isWeekScope, rangeEndDate, selectedDate]);

  const tasksPrefSyncKey = useMemo(() => {
    if (isWeekScope && weekRange) {
      return `${weekRange.startDate.format('YYYY-MM-DD')}_${weekRange.endDate.format('YYYY-MM-DD')}`;
    }
    return selectedDateKey;
  }, [isWeekScope, selectedDateKey, weekRange]);

  // Ensure we fetch tasks for the selected date plus prefetch dates.
  // Day scope must not rely solely on TodoTasksProvider's visible range — the intro
  // modal (and scheduler chat) can select dates outside that window.
  const datesToFetch = useMemo(() => {
    if (isWeekScope && weekRange) {
      const days: string[] = [];
      let cursor = weekRange.startDate.startOf('day');
      while (cursor.isSameOrBefore(weekRange.endDate, 'day')) {
        days.push(cursor.format('YYYY-MM-DD'));
        cursor = cursor.add(1, 'day');
      }
      return days;
    }

    const prefetchDates = getArrayOfXDaysForwardFromToday(PREFETCH_DATES_COUNT);
    // Include selected date and the prior day (auto-select window uses due dates
    // within ~1 day before the target). Past days are covered by overdue fetch.
    const selectedWindow = [selectedDate.subtract(1, 'day'), selectedDate].filter(
      (d) => !d.isBefore(dayjs(), 'day')
    );

    const uniqueDates = new Map<string, Dayjs>();
    for (const d of [...prefetchDates, ...selectedWindow]) {
      uniqueDates.set(d.format('YYYY-MM-DD'), d.startOf('day'));
    }

    return Array.from(uniqueDates.values())
      .sort((a, b) => (a.isAfter(b) ? 1 : -1))
      .map((d) => d.format('YYYY-MM-DD'));
  }, [isWeekScope, selectedDate, weekRange]);

  const scopedQueryProjectIds = visibleProjectIds ?? [];
  const weekQueryProjectIds = isWeekScope ? scopedQueryProjectIds : [];

  const { todoTasksByDate: scopedTodoTasksByDate, isLoading: isFetchingScopedTasks } = useTodoTasks({
    todoTasksBoardIds: scopedQueryProjectIds,
    dates: datesToFetch,
    filter: {
      labelNames: taskFilter.labelNames,
      onlyMyTask: taskFilter.onlyMyTask,
    },
  });

  const weekOverdueDateTill = useMemo(() => {
    if (!weekRange) {
      return overDueDateTill;
    }
    return weekRange.startDate.subtract(1, 'day');
  }, [overDueDateTill, weekRange]);

  const { data: weekOverdueTasks = [], isLoading: isFetchingWeekOverdueTasks } = useOverdueTasks({
    visibleProjectIds: weekQueryProjectIds,
    dueDateTill: weekOverdueDateTill.format('YYYY-MM-DD'),
    limit: WEEK_OVERDUE_LIMIT,
    filter: {
      labelNames: taskFilter.labelNames,
      hideNoDueDate: taskFilter.hideNoDueDate,
      onlyMyTask: taskFilter.onlyMyTask,
    },
  });

  const allTasks = useMemo(() => {
    // Prefer scoped fetch (covers planDate outside the kanban/todo list range).
    // Merge context tasks for day scope so any already-loaded nearby dates remain available.
    const sourceTodoTasksByDate = isWeekScope
      ? scopedTodoTasksByDate
      : [...todoTasksByDate, ...scopedTodoTasksByDate];
    const sourceOverdueTasks = isWeekScope ? weekOverdueTasks : overdueTasks;
    const safeTodoTasks = sourceTodoTasksByDate
      .flatMap((todoTasks) => todoTasks?.tasks ?? [])
      .filter(Boolean);
    const filteredTodoTasks = safeTodoTasks.filter(
      (task) => !task.completed && !task.parentOriginalTaskId
    ); // only root tasks
    const filteredOverdueTasks = (sourceOverdueTasks ?? []).filter((task) => {
      if (!task) {
        return false;
      }
      const dueDateRaw = task.dueDateTime?.date || task.dueDateTime?.datetime;
      return (
        !task.completed &&
        !task.deleted &&
        !!dueDateRaw &&
        Math.abs(dayjs(dueDateRaw).diff(dayjs(), 'day')) <= OVERDUE_TASK_RECENT_DAY_THRESHOLD
      );
    });

    const weekTasks = safeTodoTasks
      .filter((task) => !task.completed && !task.parentOriginalTaskId)
      .filter((task) => {
        if (!isWeekScope || !weekRange) {
          return true;
        }
        const dueDateRaw = task.dueDateTime?.date || task.dueDateTime?.datetime;
        if (!dueDateRaw) {
          return false;
        }
        const dueDate = dayjs(dueDateRaw);
        return (
          dueDate.isSameOrAfter(weekRange.startDate, 'day') &&
          dueDate.isSameOrBefore(weekRange.endDate, 'day')
        );
      });

    return unionBy(isWeekScope ? weekTasks : filteredTodoTasks, filteredOverdueTasks, 'taskId');
  }, [
    isWeekScope,
    overdueTasks,
    scopedTodoTasksByDate,
    todoTasksByDate,
    weekOverdueTasks,
    weekRange,
  ]);

  // Initialize the preferences
  useEffect(() => {
    if (!schedulerPreferences || isEmpty(schedulerPreferences)) {
      return;
    }
    // Work hours and Timeboxes are stored in scheduler.preferences
    dispatchWorkHourPrefItem({
      type: 'initialize',
      payload: transformDBPrefToWorkHourPref(schedulerPreferences),
    });
    // Timeboxes are stored in scheduler.preferences
    dispatchTimeBoxPrefItems({
      type: 'initialize',
      payload: transformDBPrefToTimeboxPref(schedulerPreferences),
    });
  }, [schedulerPreferences]);

  // Initialize the routine once the query has settled, and keep loading true until
  // the reducer has been seeded (avoids confirming with an empty routinePrefItem).
  useEffect(() => {
    if (isRoutineLoading) {
      setRoutinePrefSynced(false);
      return;
    }
    dispatchRoutinePrefItem({
      type: 'initialize',
      payload: transformDBPrefToRoutine(routine?.routineActivities ?? []),
    });
    setRoutinePrefSynced(true);
  }, [routine?.routineActivities, isRoutineLoading]);

  // Initialize the tasks once the scoped fetch for selectedDate has settled, so
  // callers (e.g. PlanMyDayIntroModal confirm) never read prefs for the wrong date.
  useEffect(() => {
    const stillLoadingTasks = isWeekScope
      ? isFetchingScopedTasks || isFetchingWeekOverdueTasks
      : isFetchingScopedTasks || isFetchingOverdueTasks;
    if (stillLoadingTasks) {
      return;
    }

    let nextTasksPrefState = transformTodosToTaskPref(
      allTasks,
      (isWeekScope && weekRange ? weekRange.endDate : selectedDate).format('YYYY-MM-DD'),
      tasksPrefItemsRef.current
    );

    if (isWeekScope && weekRange) {
      nextTasksPrefState = Object.entries(nextTasksPrefState).reduce(
        (acc, [taskId, task]) => {
          const previousTask = tasksPrefItemsRef.current[taskId];
          if (typeof previousTask?.selected === 'boolean') {
            acc[taskId] = task;
            return acc;
          }

          const dueDateRaw = task.dueDateTime?.date || task.dueDateTime?.datetime;
          const isInSelectedWeekRange =
            !!dueDateRaw &&
            dayjs(dueDateRaw).isSameOrAfter(weekRange.startDate, 'day') &&
            dayjs(dueDateRaw).isSameOrBefore(weekRange.endDate, 'day');

          acc[taskId] = {
            ...task,
            selected: isInSelectedWeekRange ? true : task.selected,
          };

          return acc;
        },
        {} as TasksPrefItemState
      );
    }

    tasksPrefItemsRef.current = nextTasksPrefState;
    dispatchTasksPrefItems({
      type: 'initialize',
      payload: nextTasksPrefState,
    });
    setTasksPrefSyncedKey(tasksPrefSyncKey);
  }, [
    selectedDate,
    tasksPrefSyncKey,
    isWeekScope,
    weekRange,
    JSON.stringify(allTasks),
    isFetchingScopedTasks,
    isFetchingWeekOverdueTasks,
    isFetchingOverdueTasks,
  ]);

  useEffect(() => {
    tasksPrefItemsRef.current = tasksPrefItems;
  }, [tasksPrefItems]);

  const handleAddRoutineActivity = (pref: FirestoreRoutineActivity) => {
    dispatchRoutinePrefItem({ type: 'add', payload: pref });
    addRoutineActivity(pref);
  };

  const handleDeleteRoutine = async (activityId: string) => {
    if (!routine?.routineId) return;
    dispatchRoutinePrefItem({ type: 'delete', activityId });
    await hideRoutine({ routineId: routine.routineId, activityId });
  };

  const isTasksLoading =
    (isWeekScope
      ? isFetchingScopedTasks || isFetchingWeekOverdueTasks
      : isFetchingScopedTasks || isFetchingOverdueTasks) ||
    tasksPrefSyncedKey !== tasksPrefSyncKey;
  const isRoutinePrefLoading = isRoutineLoading || !routinePrefSynced;

  return {
    timeBoxPrefItems,
    routinePrefItem,
    tasksPrefItems,
    workHourPrefItem,
    isRoutineLoading: isRoutinePrefLoading,
    isPreferenceLoading,
    isTasksLoading,
    schedulerPreferences,
    routine,
    dispatchTimeBoxPrefItems,
    dispatchRoutinePrefItem,
    dispatchTasksPrefItems,
    dispatchWorkHourPrefItem,
    handleAddRoutineActivity,
    handleDeleteRoutine,
    updatePreferences,
  };
};
